/**
 * Live updates.
 *
 * Subscribes to the server's event stream and invalidates the affected query
 * caches. Because events carry identifiers rather than rows, an out-of-order
 * or duplicated event costs one refetch and can never write stale data into
 * the cache — which is why this is safe to leave running for a whole shift.
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { realtimeEvent } from "@soi/contracts";
import { queryKeys } from "./queries.js";

export function useRealtime(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    // EventSource reconnects on its own after a network blip, which is the
    // main reason to prefer it over a hand-rolled WebSocket here.
    const source = new EventSource("/api/events");

    const handle = (raw: MessageEvent<string>) => {
      const parsed = realtimeEvent.safeParse(JSON.parse(raw.data));
      if (!parsed.success) return;
      const event = parsed.data;

      switch (event.type) {
        case "reservation.created":
        case "reservation.cancelled":
          // The building's slot grid and the summary counts both move.
          void queryClient.invalidateQueries({ queryKey: queryKeys.site(event.siteSlug) });
          void queryClient.invalidateQueries({ queryKey: queryKeys.sites });
          void queryClient.invalidateQueries({ queryKey: queryKeys.myDeliveries });
          void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
          void queryClient.invalidateQueries({ queryKey: queryKeys.adminSites });
          void queryClient.invalidateQueries({
            queryKey: queryKeys.adminReservations(event.siteSlug),
          });
          break;
        case "delivery.toggled":
          void queryClient.invalidateQueries({ queryKey: queryKeys.myDeliveries });
          void queryClient.invalidateQueries({ queryKey: queryKeys.stock });
          void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
          // A field validation moves the booking to "terminée", so the
          // management screen is stale too — this is the same event seen from
          // the other end of the deployment.
          void queryClient.invalidateQueries({ queryKey: queryKeys.adminSites });
          void queryClient.invalidateQueries({
            queryKey: queryKeys.adminReservations(event.siteSlug),
          });
          break;
        case "reservation.fulfilled":
          void queryClient.invalidateQueries({ queryKey: queryKeys.adminSites });
          void queryClient.invalidateQueries({
            queryKey: queryKeys.adminReservations(event.siteSlug),
          });
          void queryClient.invalidateQueries({ queryKey: queryKeys.myDeliveries });
          void queryClient.invalidateQueries({ queryKey: queryKeys.stock });
          void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
          break;
        case "site.updated":
          void queryClient.invalidateQueries({ queryKey: queryKeys.stock });
          void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
          break;
        case "slots.added":
          // New capacity for a building: the public range and the admin list
          // of buildings both need to see it, same as a booking would.
          void queryClient.invalidateQueries({ queryKey: queryKeys.site(event.siteSlug) });
          void queryClient.invalidateQueries({ queryKey: queryKeys.sites });
          void queryClient.invalidateQueries({ queryKey: queryKeys.adminSites });
          void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
          break;
      }
    };

    for (const type of [
      "reservation.created",
      "reservation.cancelled",
      "reservation.fulfilled",
      "delivery.toggled",
      "site.updated",
      "slots.added",
    ]) {
      source.addEventListener(type, handle as EventListener);
    }

    return () => source.close();
  }, [queryClient]);
}
